import React, { useState, useRef, useEffect } from 'react';
import { runsAPI } from '../utils/api';
import { Button } from './ui/button';
import { Send, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function ChatPanel({ projectId, stack, currentRunGoal }) {
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const handleSend = async () => {
    const text = message.trim();
    if (!text || sending) return;

    setHistory((prev) => [...prev, { role: 'user', content: text }]);
    setMessage('');
    setSending(true);

    try {
      // Create a new run to improve the current project
      const res = await runsAPI.create({
        goal: text,
        stack,
        source_run_id: projectId,
        max_steps: 20,
        daily_budget_eur: 5.0,
      });

      setHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Improvement run launched (${res.data.id.slice(0, 8)}). Redirecting...`,
        },
      ]);

      // Navigate to the new run
      setTimeout(() => navigate(`/projects/${res.data.id}`), 1500);
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to send';
      setHistory((prev) => [...prev, { role: 'error', content: errMsg }]);
      toast.error(errMsg);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Chat</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {history.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
            <MessageSquare className="w-8 h-8 opacity-20" />
            <p className="text-xs text-center">
              Describe modifications to apply to this project. A new improvement run will be created.
            </p>
          </div>
        )}
        {history.map((msg, idx) => (
          <div
            key={idx}
            className={`text-sm rounded-lg px-3 py-2 max-w-[90%] ${
              msg.role === 'user'
                ? 'ml-auto bg-primary text-primary-foreground'
                : msg.role === 'error'
                ? 'bg-destructive/10 text-destructive'
                : 'bg-muted'
            }`}
          >
            <p className="whitespace-pre-wrap text-xs">{msg.content}</p>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border p-2">
        <div className="flex items-end gap-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe changes..."
            rows={2}
            className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            disabled={sending}
          />
          <Button
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleSend}
            disabled={!message.trim() || sending}
          >
            {sending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
